// Lasso-select + classify editor for a loaded point set. Mirrors mountThreePreview's
// scene setup (see index.js) but keeps world XYZ around for screen-space picking and
// owns a small self-contained toolbar so it never has to be rebuilt by the parent
// panel's template-string re-render.

function hexToRgb01(hex) {
  const h = String(hex || "#808080").replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const n = parseInt(full, 16) || 0;
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
}

// Freehand lasso drags sample far more vertices than needed for a point-in-polygon
// test; an unsimplified 1000+ vertex ring against 10k+ points is a real stall.
function simplifyPolygon(points, minDist = 6) {
  if (points.length < 3) return points;
  const out = [points[0]];
  for (let i = 1; i < points.length; i += 1) {
    const last = out[out.length - 1];
    const dx = points[i].x - last.x;
    const dy = points[i].y - last.y;
    if ((dx * dx) + (dy * dy) >= minDist * minDist) out.push(points[i]);
  }
  return out.length >= 3 ? out : points;
}

// Coordinate-agnostic ray-casting test (mirrors src/tools/line-planning/generate.js's
// pointInPolygon) — operates on plain {x,y} screen-pixel points here.
function pointInPolygon(pt, ring) {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i].x;
    const yi = ring[i].y;
    const xj = ring[j].x;
    const yj = ring[j].y;
    const intersect = (yi > pt.y) !== (yj > pt.y)
      && pt.x < (((xj - xi) * (pt.y - yi)) / (yj - yi + Number.EPSILON)) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

export function mountClassifySession(host, points, libs, options = {}) {
  const { THREE, OrbitControls } = libs;
  const classes = Array.isArray(options.classes) ? options.classes : [];
  const classColor = typeof options.classColor === "function" ? options.classColor : () => "#808080";
  const onSelectionChange = typeof options.onSelectionChange === "function" ? options.onSelectionChange : () => {};
  const onEditsChange = typeof options.onEditsChange === "function" ? options.onEditsChange : () => {};

  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0x030712);
  const camera = new THREE.PerspectiveCamera(62, 1, 0.1, 1e9);
  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  if ("outputColorSpace" in renderer && THREE?.SRGBColorSpace) {
    renderer.outputColorSpace = THREE.SRGBColorSpace;
  } else if ("outputEncoding" in renderer && THREE?.sRGBEncoding) {
    renderer.outputEncoding = THREE.sRGBEncoding;
  }

  host.innerHTML = "";
  host.style.position = "relative";
  renderer.domElement.style.cssText = "position:absolute; inset:0;";
  host.appendChild(renderer.domElement);

  const controls = new OrbitControls(camera, renderer.domElement);
  controls.enableDamping = true;
  controls.dampingFactor = 0.08;

  // World-space bounds/center, same recentring mountThreePreview does — the GPU
  // position buffer is local-frame only, so world XYZ is tracked separately below.
  const bounds = { minX: Infinity, minY: Infinity, minZ: Infinity, maxX: -Infinity, maxY: -Infinity, maxZ: -Infinity };
  for (const p of points) {
    const x = Number(p?.x);
    const y = Number(p?.y);
    const z = Number(p?.z);
    if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) continue;
    if (x < bounds.minX) bounds.minX = x;
    if (y < bounds.minY) bounds.minY = y;
    if (z < bounds.minZ) bounds.minZ = z;
    if (x > bounds.maxX) bounds.maxX = x;
    if (y > bounds.maxY) bounds.maxY = y;
    if (z > bounds.maxZ) bounds.maxZ = z;
  }
  const center = {
    x: (bounds.minX + bounds.maxX) / 2 || 0,
    y: (bounds.minY + bounds.maxY) / 2 || 0,
    z: (bounds.minZ + bounds.maxZ) / 2 || 0,
  };
  const maxSpan = Math.max(1e-6, bounds.maxX - bounds.minX, bounds.maxY - bounds.minY, bounds.maxZ - bounds.minZ);

  const n = points.length;
  const positions = new Float32Array(n * 3);
  const colors = new Float32Array(n * 3);
  const classification = new Int16Array(n);
  const worldX = new Float64Array(n);
  const worldY = new Float64Array(n);
  const worldZ = new Float64Array(n);
  const selected = new Uint8Array(n);

  for (let i = 0; i < n; i += 1) {
    const p = points[i];
    const x = Number(p?.x);
    const y = Number(p?.y);
    const z = Number(p?.z);
    const vx = Number.isFinite(x) ? x : center.x;
    const vy = Number.isFinite(y) ? y : center.y;
    const vz = Number.isFinite(z) ? z : center.z;
    worldX[i] = vx; worldY[i] = vy; worldZ[i] = vz;
    positions[(i * 3) + 0] = vx - center.x;
    positions[(i * 3) + 1] = vy - center.y;
    positions[(i * 3) + 2] = vz - center.z;
    const cls = Number.isFinite(Number(p?.classification)) ? Number(p.classification) : 0;
    classification[i] = cls;
    const [r, g, b] = hexToRgb01(classColor(cls));
    colors[(i * 3) + 0] = r; colors[(i * 3) + 1] = g; colors[(i * 3) + 2] = b;
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute("color", new THREE.BufferAttribute(colors, 3));
  geometry.computeBoundingSphere();
  const material = new THREE.PointsMaterial({
    size: Math.max(maxSpan * 0.0024, 0.6),
    sizeAttenuation: true,
    vertexColors: true,
  });
  const cloud = new THREE.Points(geometry, material);
  scene.add(cloud);
  scene.add(new THREE.AmbientLight(0xffffff, 1));

  const distance = Math.max(maxSpan * 1.8, 20);
  camera.position.set(distance, -distance * 0.9, distance * 0.65);
  controls.target.set(0, 0, 0);
  controls.update();

  // --- lasso overlay canvas (captures drag when in lasso mode, transparent to
  // OrbitControls otherwise) ---
  const lassoCanvas = document.createElement("canvas");
  lassoCanvas.style.cssText = "position:absolute; inset:0; cursor:crosshair;";
  host.appendChild(lassoCanvas);
  const lctx = lassoCanvas.getContext("2d");

  let lassoMode = true;
  let drawing = false;
  let lassoPts = [];
  const pendingEdits = new Map();

  function redrawLasso() {
    lctx.clearRect(0, 0, lassoCanvas.width, lassoCanvas.height);
    if (lassoPts.length < 2) return;
    lctx.strokeStyle = "#22d3ee";
    lctx.lineWidth = 2;
    lctx.setLineDash([4, 3]);
    lctx.beginPath();
    lctx.moveTo(lassoPts[0].x, lassoPts[0].y);
    for (let i = 1; i < lassoPts.length; i += 1) lctx.lineTo(lassoPts[i].x, lassoPts[i].y);
    lctx.closePath();
    lctx.stroke();
  }

  const scratch = new THREE.Vector3();
  const pixel = { x: 0, y: 0 };
  function projectToPixel(x, y, z) {
    scratch.set(x - center.x, y - center.y, z - center.z);
    scratch.project(camera);
    pixel.x = ((scratch.x * 0.5) + 0.5) * lassoCanvas.width;
    pixel.y = (1 - ((scratch.y * 0.5) + 0.5)) * lassoCanvas.height;
    return scratch.z >= -1 && scratch.z <= 1;
  }

  function applyLassoSelection(ring) {
    camera.updateMatrixWorld(true);
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const p of ring) {
      if (p.x < minX) minX = p.x;
      if (p.x > maxX) maxX = p.x;
      if (p.y < minY) minY = p.y;
      if (p.y > maxY) maxY = p.y;
    }
    let count = 0;
    for (let i = 0; i < n; i += 1) {
      const inFrustum = projectToPixel(worldX[i], worldY[i], worldZ[i]);
      if (!inFrustum || pixel.x < minX || pixel.x > maxX || pixel.y < minY || pixel.y > maxY) {
        selected[i] = 0;
        continue;
      }
      const inside = pointInPolygon(pixel, ring);
      selected[i] = inside ? 1 : 0;
      if (inside) count += 1;
    }
    return count;
  }

  function refreshSelectionColors() {
    const colorAttr = geometry.attributes.color;
    for (let i = 0; i < n; i += 1) {
      const [r, g, b] = selected[i] ? [1, 1, 1] : hexToRgb01(classColor(classification[i]));
      colorAttr.array[(i * 3) + 0] = r;
      colorAttr.array[(i * 3) + 1] = g;
      colorAttr.array[(i * 3) + 2] = b;
    }
    colorAttr.needsUpdate = true;
  }

  function clearSelection() {
    for (let i = 0; i < n; i += 1) selected[i] = 0;
    refreshSelectionColors();
    onSelectionChange(0);
  }

  function onPointerDown(e) {
    if (!lassoMode) return;
    drawing = true;
    lassoPts = [{ x: e.offsetX, y: e.offsetY }];
    lassoCanvas.setPointerCapture?.(e.pointerId);
  }
  function onPointerMove(e) {
    if (!drawing) return;
    lassoPts.push({ x: e.offsetX, y: e.offsetY });
    redrawLasso();
  }
  function onPointerUp() {
    if (!drawing) return;
    drawing = false;
    const ring = simplifyPolygon(lassoPts, 6);
    lassoPts = [];
    lctx.clearRect(0, 0, lassoCanvas.width, lassoCanvas.height);
    if (ring.length >= 3) {
      const count = applyLassoSelection(ring);
      refreshSelectionColors();
      onSelectionChange(count);
    }
  }
  function onKeyDown(e) {
    if (e.key !== "Escape") return;
    drawing = false;
    lassoPts = [];
    redrawLasso();
  }

  lassoCanvas.addEventListener("pointerdown", onPointerDown);
  lassoCanvas.addEventListener("pointermove", onPointerMove);
  lassoCanvas.addEventListener("pointerup", onPointerUp);
  window.addEventListener("keydown", onKeyDown);

  function setMode(mode) {
    lassoMode = mode === "lasso";
    lassoCanvas.style.pointerEvents = lassoMode ? "auto" : "none";
    controls.enabled = !lassoMode;
    modeBtn.textContent = lassoMode ? "Mode: Lasso" : "Mode: Orbit";
  }

  // --- self-contained toolbar, built with raw DOM so it survives independently of
  // the parent panel's template-string re-renders ---
  const toolbar = document.createElement("div");
  toolbar.style.cssText = "position:absolute; top:6px; left:6px; right:6px; display:flex; flex-wrap:wrap; gap:6px; align-items:center; background:rgba(2,6,23,.78); border:1px solid rgba(148,163,184,.25); border-radius:8px; padding:6px 8px; font-size:11px; color:#e2e8f0; z-index:2;";
  host.appendChild(toolbar);

  const modeBtn = document.createElement("button");
  modeBtn.className = "btn";
  modeBtn.style.cssText = "font-size:11px; padding:3px 8px;";
  modeBtn.onclick = () => setMode(lassoMode ? "orbit" : "lasso");
  toolbar.appendChild(modeBtn);
  setMode("lasso");

  const classSelect = document.createElement("select");
  classSelect.style.cssText = "font-size:11px;";
  for (const c of classes) {
    const opt = document.createElement("option");
    opt.value = String(c.id);
    opt.textContent = `${c.id} — ${c.name}`;
    classSelect.appendChild(opt);
  }
  toolbar.appendChild(classSelect);

  const applyBtn = document.createElement("button");
  applyBtn.className = "btn primary";
  applyBtn.textContent = "Apply to Selection";
  applyBtn.style.cssText = "font-size:11px; padding:3px 8px;";
  applyBtn.onclick = () => {
    const targetClass = Number(classSelect.value);
    let applied = 0;
    for (let i = 0; i < n; i += 1) {
      if (!selected[i]) continue;
      classification[i] = targetClass;
      pendingEdits.set(i, { x: worldX[i], y: worldY[i], z: worldZ[i], classification: targetClass });
      applied += 1;
    }
    clearSelection();
    onEditsChange(pendingEdits.size);
    statusLabel.textContent = applied
      ? `Applied class ${targetClass} to ${applied} point(s). Pending edits: ${pendingEdits.size}.`
      : "Nothing selected — draw a lasso first.";
  };
  toolbar.appendChild(applyBtn);

  const clearBtn = document.createElement("button");
  clearBtn.className = "btn";
  clearBtn.textContent = "Clear Selection";
  clearBtn.style.cssText = "font-size:11px; padding:3px 8px;";
  clearBtn.onclick = () => {
    clearSelection();
    statusLabel.textContent = "Selection cleared.";
  };
  toolbar.appendChild(clearBtn);

  const exportBtn = document.createElement("button");
  exportBtn.className = "btn";
  exportBtn.textContent = "Export Edits (JSON)";
  exportBtn.style.cssText = "font-size:11px; padding:3px 8px;";
  exportBtn.onclick = () => {
    const edits = [...pendingEdits.values()];
    const blob = new Blob([JSON.stringify(edits, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "classification-edits.json";
    a.click();
    URL.revokeObjectURL(url);
  };
  toolbar.appendChild(exportBtn);

  const statusLabel = document.createElement("div");
  statusLabel.style.cssText = "flex-basis:100%; opacity:.8;";
  statusLabel.textContent = "Drag to lasso points, pick a class, then Apply.";
  toolbar.appendChild(statusLabel);

  function resize() {
    const w = Math.max(120, host.clientWidth || 0);
    const h = Math.max(120, host.clientHeight || 0);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    renderer.setSize(w, h, false);
    lassoCanvas.width = w;
    lassoCanvas.height = h;
  }

  let raf = 0;
  let paused = false;
  let destroyed = false;
  const loop = () => {
    if (destroyed || paused) { raf = 0; return; }
    raf = requestAnimationFrame(loop);
    controls.update();
    renderer.render(scene, camera);
  };
  const startLoop = () => { if (!destroyed && !paused && !raf) loop(); };
  const setPaused = (value) => {
    paused = !!value;
    if (paused && raf) { cancelAnimationFrame(raf); raf = 0; return; }
    startLoop();
  };

  let ro = null;
  const onWindowResize = () => resize();
  if (typeof ResizeObserver === "function") {
    ro = new ResizeObserver(() => resize());
    ro.observe(host);
  } else {
    window.addEventListener("resize", onWindowResize);
  }
  resize();
  startLoop();

  const destroy = () => {
    destroyed = true;
    try { cancelAnimationFrame(raf); } catch {}
    try { ro?.disconnect?.(); } catch {}
    try { window.removeEventListener("resize", onWindowResize); } catch {}
    try { window.removeEventListener("keydown", onKeyDown); } catch {}
    try { lassoCanvas.removeEventListener("pointerdown", onPointerDown); } catch {}
    try { lassoCanvas.removeEventListener("pointermove", onPointerMove); } catch {}
    try { lassoCanvas.removeEventListener("pointerup", onPointerUp); } catch {}
    try { controls.dispose(); } catch {}
    try { geometry.dispose(); } catch {}
    try { material.dispose(); } catch {}
    try { renderer.dispose(); } catch {}
    try { host.innerHTML = ""; } catch {}
  };

  return {
    destroy,
    setPaused,
    getPendingEditsCount: () => pendingEdits.size,
    exportEditsJson: () => JSON.stringify([...pendingEdits.values()], null, 2),
  };
}
